import React, { useEffect, useMemo, useState } from 'react';
import { COLORS } from '../../../../core/constants/colors';
import { CompanyPicker, CompanyOption } from '../../../../core/components/CompanyPicker';

interface InvitePartnerModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSend: (payload: InvitePayload) => Promise<void> | void;
  companies: CompanyOption[];
  isLoadingCompanies?: boolean;
  isSubmitting?: boolean;
}

export interface InvitePayload {
  email: string;
  first_name: string;
  last_name: string;
  /** Set when the admin picked an existing portfolio company. */
  company_id?: number;
  /** Set when the admin chose "Create new company" in the picker. */
  new_company?: {
    name: string;
    /** Optional — backend derives from email domain when blank. */
    domain?: string;
  };
  notes?: string;
}

type CompanyMode = 'pick' | 'create';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const emptyState = {
  email: '',
  first_name: '',
  last_name: '',
  selected_company_id: null as number | null,
  new_company_name: '',
  new_company_domain: '',
  notes: '',
};

export const InvitePartnerModal: React.FC<InvitePartnerModalProps> = ({
  isOpen,
  onClose,
  onSend,
  companies,
  isLoadingCompanies = false,
  isSubmitting = false,
}) => {
  const [formValues, setFormValues] = useState(emptyState);
  const [companyMode, setCompanyMode] = useState<CompanyMode>('pick');
  const [hasSubmitted, setHasSubmitted] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setFormValues(emptyState);
      setCompanyMode('pick');
      setHasSubmitted(false);
    }
  }, [isOpen]);

  const emailError = useMemo(() => {
    if (!hasSubmitted) return '';
    if (!formValues.email.trim()) return 'Email is required';
    if (!EMAIL_REGEX.test(formValues.email.trim())) return 'Enter a valid email address';
    return '';
  }, [formValues.email, hasSubmitted]);

  const firstNameError = useMemo(() => {
    if (!hasSubmitted) return '';
    if (!formValues.first_name.trim()) return 'First name is required';
    return '';
  }, [formValues.first_name, hasSubmitted]);

  const lastNameError = useMemo(() => {
    if (!hasSubmitted) return '';
    if (!formValues.last_name.trim()) return 'Last name is required';
    return '';
  }, [formValues.last_name, hasSubmitted]);

  const companyError = useMemo(() => {
    if (!hasSubmitted) return '';
    if (companyMode === 'pick') {
      if (formValues.selected_company_id == null) return 'Pick a company or create a new one';
      return '';
    }
    if (!formValues.new_company_name.trim()) return 'Company name is required';
    return '';
  }, [companyMode, formValues.selected_company_id, formValues.new_company_name, hasSubmitted]);

  const canSubmit = useMemo(() => {
    const baseValid =
      !!formValues.email.trim() &&
      !!formValues.first_name.trim() &&
      !!formValues.last_name.trim() &&
      EMAIL_REGEX.test(formValues.email.trim());
    const companyValid =
      companyMode === 'pick'
        ? formValues.selected_company_id != null
        : !!formValues.new_company_name.trim();
    return baseValid && companyValid;
  }, [formValues, companyMode]);

  const derivedDomainHint = useMemo(() => {
    const at = formValues.email.indexOf('@');
    if (at < 0) return '';
    return formValues.email.slice(at + 1).toLowerCase();
  }, [formValues.email]);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setHasSubmitted(true);
    if (!canSubmit) return;

    const payload: InvitePayload = {
      email: formValues.email.trim(),
      first_name: formValues.first_name.trim(),
      last_name: formValues.last_name.trim(),
      notes: formValues.notes?.trim() || undefined,
    };

    if (companyMode === 'pick' && formValues.selected_company_id != null) {
      payload.company_id = formValues.selected_company_id;
    } else if (companyMode === 'create') {
      const trimmedDomain = formValues.new_company_domain.trim();
      payload.new_company = {
        name: formValues.new_company_name.trim(),
        ...(trimmedDomain ? { domain: trimmedDomain } : {}),
      };
    }

    await onSend(payload);
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 z-50 transition-opacity"
        onClick={onClose}
      />

      {/* Side Drawer */}
      <div className="fixed right-0 top-0 h-full w-full max-w-md bg-white shadow-xl z-50 transform transition-transform duration-300 ease-in-out overflow-y-auto">
        <div className="h-full flex flex-col">
          {/* Header */}
          <div className="px-6 py-5 border-b flex items-center justify-between flex-shrink-0" style={{ borderColor: '#E5E7EB' }}>
            <div className="flex-1">
              <h2 className="text-xl font-bold" style={{ color: COLORS.primary.persianIndigo }}>
                Invite Partner
              </h2>
              <p className="text-sm mt-1" style={{ color: COLORS.primary.persianIndigo, opacity: 0.7 }}>
                Send an invitation to create an account
              </p>
            </div>
            <button
              onClick={onClose}
              className="w-8 h-8 rounded-lg flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-all flex-shrink-0"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Form Content */}
          <div className="flex-1 px-6 py-6 overflow-y-auto">
            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: COLORS.primary.persianIndigo }}>
                  Email Address <span style={{ color: COLORS.extended.redSalsa }}>*</span>
                </label>
                <input
                  type="email"
                  placeholder="partner@company.com"
                  value={formValues.email}
                  onChange={(event) => setFormValues((prev) => ({ ...prev, email: event.target.value }))}
                  className="w-full px-4 py-3 rounded-lg border-2 focus:outline-none transition-all"
                  style={{
                    borderColor: emailError ? COLORS.extended.redSalsa : 'transparent',
                    backgroundColor: '#F9FAFB',
                  }}
                  onFocus={(e) => {
                    if (!emailError) e.target.style.borderColor = COLORS.admin.primary;
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = emailError ? COLORS.extended.redSalsa : 'transparent';
                  }}
                  required
                />
                {emailError && (
                  <p className="text-xs mt-1" style={{ color: COLORS.extended.redSalsa }}>
                    {emailError}
                  </p>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: COLORS.primary.persianIndigo }}>
                    First Name <span style={{ color: COLORS.extended.redSalsa }}>*</span>
                  </label>
                  <input
                    type="text"
                    placeholder="John"
                    value={formValues.first_name}
                    onChange={(event) => setFormValues((prev) => ({ ...prev, first_name: event.target.value }))}
                    className="w-full px-4 py-3 rounded-lg border-2 focus:outline-none transition-all"
                    style={{
                      borderColor: firstNameError ? COLORS.extended.redSalsa : 'transparent',
                      backgroundColor: '#F9FAFB',
                    }}
                    onFocus={(e) => {
                      if (!firstNameError) e.target.style.borderColor = COLORS.admin.primary;
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = firstNameError ? COLORS.extended.redSalsa : 'transparent';
                    }}
                    required
                  />
                  {firstNameError && (
                    <p className="text-xs mt-1" style={{ color: COLORS.extended.redSalsa }}>
                      {firstNameError}
                    </p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: COLORS.primary.persianIndigo }}>
                    Last Name <span style={{ color: COLORS.extended.redSalsa }}>*</span>
                  </label>
                  <input
                    type="text"
                    placeholder="Doe"
                    value={formValues.last_name}
                    onChange={(event) => setFormValues((prev) => ({ ...prev, last_name: event.target.value }))}
                    className="w-full px-4 py-3 rounded-lg border-2 focus:outline-none transition-all"
                    style={{
                      borderColor: lastNameError ? COLORS.extended.redSalsa : 'transparent',
                      backgroundColor: '#F9FAFB',
                    }}
                    onFocus={(e) => {
                      if (!lastNameError) e.target.style.borderColor = COLORS.admin.primary;
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = lastNameError ? COLORS.extended.redSalsa : 'transparent';
                    }}
                    required
                  />
                  {lastNameError && (
                    <p className="text-xs mt-1" style={{ color: COLORS.extended.redSalsa }}>
                      {lastNameError}
                    </p>
                  )}
                </div>
              </div>

              {/* Company section: picker by default, swap to inline create form on demand */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium" style={{ color: COLORS.primary.persianIndigo }}>
                    Company <span style={{ color: COLORS.extended.redSalsa }}>*</span>
                  </label>
                  {companyMode === 'create' && (
                    <button
                      type="button"
                      onClick={() => {
                        setCompanyMode('pick');
                        setFormValues((prev) => ({ ...prev, new_company_name: '', new_company_domain: '' }));
                      }}
                      className="text-xs font-medium hover:underline"
                      style={{ color: COLORS.admin.primary }}
                    >
                      ← Back to picker
                    </button>
                  )}
                </div>

                {companyMode === 'pick' ? (
                  <>
                    <CompanyPicker
                      value={formValues.selected_company_id}
                      companies={companies}
                      isLoading={isLoadingCompanies}
                      onChange={(id) => setFormValues((prev) => ({ ...prev, selected_company_id: id }))}
                      onCreateNew={() => {
                        setCompanyMode('create');
                        setFormValues((prev) => ({ ...prev, selected_company_id: null }));
                      }}
                      borderColor={companyError ? COLORS.extended.redSalsa : '#E5E7EB'}
                    />
                  </>
                ) : (
                  <div className="space-y-3 p-3 rounded-lg" style={{ backgroundColor: '#F9FAFB', border: '1px solid #E5E7EB' }}>
                    <div>
                      <label className="block text-xs font-medium mb-1.5 text-gray-700">
                        Company name <span style={{ color: COLORS.extended.redSalsa }}>*</span>
                      </label>
                      <input
                        type="text"
                        placeholder="Acme Holdings"
                        value={formValues.new_company_name}
                        onChange={(event) =>
                          setFormValues((prev) => ({ ...prev, new_company_name: event.target.value }))
                        }
                        className="w-full px-3 py-2 rounded-lg border focus:outline-none focus:border-blue-500 transition-all bg-white"
                        style={{
                          borderColor: companyError && !formValues.new_company_name.trim()
                            ? COLORS.extended.redSalsa
                            : '#E5E7EB',
                        }}
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium mb-1.5 text-gray-700">
                        Domain <span className="text-gray-400 font-normal">(optional)</span>
                      </label>
                      <input
                        type="text"
                        placeholder={derivedDomainHint || 'company.com'}
                        value={formValues.new_company_domain}
                        onChange={(event) =>
                          setFormValues((prev) => ({ ...prev, new_company_domain: event.target.value }))
                        }
                        className="w-full px-3 py-2 rounded-lg border focus:outline-none focus:border-blue-500 transition-all bg-white"
                        style={{ borderColor: '#E5E7EB' }}
                      />
                      {derivedDomainHint && (
                        <p className="text-xs mt-1 text-gray-500">
                          Leave blank to use <span className="font-mono">{derivedDomainHint}</span> from the partner's email.
                        </p>
                      )}
                    </div>
                  </div>
                )}
                {companyError && (
                  <p className="text-xs mt-1" style={{ color: COLORS.extended.redSalsa }}>
                    {companyError}
                  </p>
                )}
              </div>
            </form>
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t flex-shrink-0 flex items-center gap-3" style={{ borderColor: '#E5E7EB' }}>
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2.5 rounded-lg font-medium transition-all"
              style={{ border: `1px solid #E5E7EB`, color: COLORS.primary.persianIndigo }}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={isSubmitting || !canSubmit}
              className="flex-1 px-4 py-2.5 rounded-lg font-semibold text-white transition-all disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              style={{ backgroundColor: COLORS.admin.primary }}
            >
              {isSubmitting ? (
                <>
                  <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Sending...
                </>
              ) : (
                <>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                  </svg>
                  Send Invite
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};
